import React from 'react'
import { Button, Col, Divider, Form, message, Row, Select, Skeleton, Typography, Collapse, Result } from 'antd'
import { PrinterOutlined } from '@ant-design/icons'
import { SketchPicker } from 'react-color'
import { FormComponent } from '../../../BaseComponent'
import { ShelterContextData } from '../../../../context/ShelterContext'
import { ShelterSettingContextData } from '../../../../context/ShelterSettingContext'
import { ImageUpload } from '../../../Upload'
import { ShelterKioskLogoUpdateRequest } from '../../../../services/api/interfaces/ApiRequest'
import Config from '../../../../config'
import IShelterKioskSetting from '../../../../interfaces/IShelterKioskSetting'
import { KioskConstants } from '../../../../helpers/enums/Kiosk'
import GenericList from '../../../GenericList'
import { QrCodeProvider, QrCodeConsumer } from '../../../../context/QrCodeContext'
import QrCode from './QrCode'

export type InPersonProps = {
  uuid: string,

  context: ShelterContextData,
  settingsContext: ShelterSettingContextData
}

export default class InPerson extends FormComponent<InPersonProps> {

  state = {
    loaded: !!this.props.context.shelter,

    fileList: [] as any,
    colorPrimary: '',
    colorSecondary: '',
  }

  private onPrintAll: any

  async componentDidMount() {
    const { uuid, context, settingsContext } = this.props

    if (uuid && !context.fetching && !context.shelter) {
      await context.requestShelterFind({
        search: {
          uuid: uuid,
          contract_options: {
            with: {
              deleted: false
            }
          },
          organization_question_shelters: {
            with: {
              deleted: false,
              kiosk_reason_id: null,
              question_shelter_species: {
                with: true
              }
            }
          },
          organization_branches: {
            with: {
              deleted: false
            }
          }
        },
        options: {
          _force_ids: true
        }
      })

      await settingsContext.requestShelterSettingList({
        search: {
          organization_shelter: {
            has: {
              uuid
            }
          }
        }
      })
    }

    await this.getKioskSettings()

    this.setState({
      loaded: true
    })
  }

  private getKioskSettings = () => {
    return new Promise((resolve) => {
      const { context, settingsContext } = this.props
      let newState = Object.assign({}, this.state)

      if (context.shelter) {
        const { uuid, kiosk_logo } = context.shelter

        if (kiosk_logo && kiosk_logo.length > 0) {
          newState.fileList.push({
            uid: uuid,
            url: `${Config.cdnImagesBase}organization/shelter/kiosk_logo/AUTOx200/${kiosk_logo}`,
            status: 'done'
          })
        }
      }

      if (settingsContext.shelterSettings) {
        const kioskSettings = settingsContext.shelterSettings.find(v => v.type_key === 'KIOSK')

        if (kioskSettings) {
          const value = kioskSettings.value as IShelterKioskSetting

          newState.colorPrimary = value.theme?.primary || ''
          newState.colorSecondary = value.theme?.secondary || ''
        }
      }

      this.setState(newState, () => resolve(true))
    })
  }

  private handleImageUploadChange = (fileList) => {
    this.setState({
      fileList
    })
  }

  private handleChangeComplete = (field: 'colorPrimary' | 'colorSecondary', value: string) => {
    this.setState({
      [field]: value
    })
  }

  private handleQrCodePrintAll = (ev: any) => {
    ev.stopPropagation()

    if (this.onPrintAll) {
      this.onPrintAll()
    }
  }

  private handleActiveInPerson = async () => {
    const { uuid, context } = this.props
    
    try {
      await context.requestShelterActivateInPerson({
        organization_shelter_uuid: uuid
      })
      
      message.success('Shelter In Person feature activated!')
    } catch (error) {
      message.error(`Failed to activate shelter in person feature. Reason: ${error}`)
    }
  }

  private handleSave = async (values) => {
    const { uuid, context, settingsContext } = this.props
    const { fileList, colorPrimary, colorSecondary } = this.state
    const { new_visitor, existing_visitor } = values

    try {
      // Parse banner to request
      const logo = fileList[0]
      if (logo?.url?.includes('data:image')) {
        const payload: ShelterKioskLogoUpdateRequest = {
          organization_shelter_uuid: uuid,
          method: 'BASE_64',
          kiosk_logo: logo.url
        }

        await context.requestShelterKioskLogoUpdate(payload)
      }

      await settingsContext.requestShelterSettingKioskSave({
        shelter_uuid: uuid,
        type_key: 'KIOSK',
        value: {
          theme: {
            primary: colorPrimary.length > 0 ? colorPrimary : null,
            secondary: colorSecondary.length > 0 ? colorSecondary : null
          }
        }
      })

      if (new_visitor || existing_visitor) {
        await settingsContext.requestShelterSettingInPersonSave({
          shelter_uuid: uuid,
          type_key: 'APPLICATION_IN_PERSON',
          value: {
            new_visitor: new_visitor || null,
            existing_visitor: existing_visitor || []
          }
        })
      }

      message.success('Kiosk settings saved successfully!')
    } catch (err) {
      message.error(`Could not save kiosk settings. Reason ${err}`)
    }

  }

  private renderCustomSettings = () => {
    if (!this.formRef) return null

    return KioskConstants.KIOSK_CUSTOM_FIELDS.map((customSettings, index) => {
      return <GenericList key={`settings-${index}`} listName="existing_visitor" label={customSettings.label} fields={customSettings.fields || []} form={this.formRef} />
    })
  }

  render() {
    const { uuid, context, settingsContext } = this.props
    const { loaded, fileList, colorPrimary, colorSecondary } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    if (context.shelter && !context.shelter.in_person) {
      return (
        <Result
          status="404"
          title="Shelter In Person not activated"
          extra={<Button type="primary" loading={context.fetching || false} onClick={this.handleActiveInPerson}>Activate In Person Feature</Button>}
        />
      )
    }

    const inPersonSettings = settingsContext.shelterSettings?.find(v => v.type_key === 'APPLICATION_IN_PERSON')
    let values: any = {}

    if (inPersonSettings) {
      values = {
        ...values,
        ...inPersonSettings.value
      }
    }

    return (
      <Form
        name="organization_shelter_kiosk"
        initialValues={values}
        layout="vertical"
        ref={ref => {
          if (!this.formRef) {
            this.formRef = ref
            this.forceUpdate()
          }
        }}
        onFinish={this.handleSave}
      >
        <Collapse>
          <Collapse.Panel header="Kiosk logo and custom colors" key="kiosk-panel">
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Typography.Title level={3}>Logo</Typography.Title>
                <ImageUpload
                  name="banner"
                  fileList={fileList}
                  aspect={4 / 3}
                  className="kiosk-logo-uploader"
                  onChange={this.handleImageUploadChange}
                />
              </Col>

              <Col xs={24} md={12}>
                <Typography.Title level={3}>Colors</Typography.Title>
                
                <Row gutter={12}>
                  <Col xs={12}>
                    <Typography.Title level={5}>Primary</Typography.Title>
                    <SketchPicker
                      disableAlpha={true}
                      width={150}
                      color={colorPrimary}
                      onChangeComplete={(color: any) => this.handleChangeComplete('colorPrimary', color.hex)}
                    />
                  </Col>

                  <Col xs={12}>
                    <Typography.Title level={5}>Secondary</Typography.Title>
                    <SketchPicker
                      disableAlpha={true}
                      width={150}
                      color={colorSecondary}
                      onChangeComplete={(color: any) => this.handleChangeComplete('colorSecondary', color.hex)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Collapse.Panel>
          
          <Collapse.Panel header="Visitor Status Updates when Applying through Smart Kennel Card QR Code" key="settings-panel">
            <Row gutter={24}>
              <Col xs={24} sm={8}>
                <Typography.Title level={5}>New visitor</Typography.Title>

                <Form.Item
                  name="new_visitor"
                >
                  <Select placeholder="Status" options={[
                    {
                      label: 'CHECKED_IN',
                      value: 'CHECKED_IN'
                    },
                    {
                      label: 'WAIT_LIST',
                      value: 'WAIT_LIST'
                    }
                  ]} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={16}>
                {this.renderCustomSettings()}
              </Col>
            </Row>
          </Collapse.Panel>
          
          <Collapse.Panel header="Waitlist QR Code (it enables visitors under CHECKED-IN to have their status updated to WAITLIST)" key="qr-code-panel" extra={<Button type="ghost" shape="circle" icon={<PrinterOutlined />} onClick={this.handleQrCodePrintAll} />}>
            <QrCodeProvider>
              <QrCodeConsumer>
                {(ctx) => (
                  <QrCode
                    shelterUuid={uuid}
                    shelterBranches={context.shelter?.organization_branches || []} context={ctx}
                    onPrintAll={printAll => this.onPrintAll = printAll}
                  />
                )}
              </QrCodeConsumer>
            </QrCodeProvider>
          </Collapse.Panel>
        </Collapse>

        <Divider />

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={context.fetching || settingsContext.fetching || false}
          ref={(refs) => this.actionRefs.save = refs}
          block
        >
          Save
        </Button>
      </Form>
    )
  }
}
